/* istanbul ignore file */
import type { ReactNode } from 'react';
import React from 'react';
import {
  Banner,
  FloatingBannerImage,
  IconAppStore,
  IconGooglePlay,
} from '@noths/polaris-client-ribbons-design-system';
import { getContentstackImagesForWidths, getPathnameFromUrl } from '@noths/polaris-client-utils';
import type { GetServerSideProps } from 'next';

import icon1 from 'src/assets/images/AppsDownloadIcons/icon-1.svg';
import icon2 from 'src/assets/images/AppsDownloadIcons/icon-2.svg';
import icon3 from 'src/assets/images/AppsDownloadIcons/icon-3.svg';
import icon4 from 'src/assets/images/AppsDownloadIcons/icon-4.svg';
import icon5 from 'src/assets/images/AppsDownloadIcons/icon-5.svg';
import icon6 from 'src/assets/images/AppsDownloadIcons/icon-6.svg';
import { Seo } from 'src/components/atoms/Seo/Seo';
import { getFeatureFlagsWithMatchers } from 'src/featureFlags/getFeatureFlagsWithMatchers';
import { fetchGlobalContent } from 'src/services/contentstack/fetchGlobalContent';
import * as styles from 'src/styles/DownloadTheApp.styles';
import { trackDownloadAppClick } from 'src/tracking/components/apps';

const appStoreAppsFlyerUrl = 'https://notonthehighstreet.onelink.me/XbOV/af2df437';
const googlePlayAppsFlyerUrl = 'https://notonthehighstreet.onelink.me/XbOV/7f57a61e';

const appQRCode =
  'https://images.contentstack.io/v3/assets/blt63f2f7afd19b09c2/blt2c96899845f8f729/68222007f2fd7ab4910278c9/apps-qr-code.png';

interface IconListProps {
  children: ReactNode;
  start?: number;
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { req } = context;
  const featureFlags = (req && getFeatureFlagsWithMatchers(req)) || {};
  const pathname = getPathnameFromUrl(req.url ?? '');
  const [siteChromeContent] = await fetchGlobalContent({
    featureFlags,
    urlPath: pathname,
  });

  return {
    props: {
      siteChromeContent,
    },
  };
};

const BannerImage = ({ imgUrl }: { imgUrl: string }) => (
  <FloatingBannerImage
    imageAlt="Not On The Highstreet mobile app"
    imageHeight={216}
    imageWidth={360}
    images={getContentstackImagesForWidths(imgUrl, [360, 500, 700, 900, 1132], {
      aspectRatio: '16:9',
    })}
    offset="top"
  />
);

const HeroBanner = () => (
  <Banner backgroundColor="#f1f0fc">
    <div css={styles.container}>
      <div css={styles.imageWrapper}>
        <BannerImage imgUrl="https://images.contentstack.io/v3/assets/blt63f2f7afd19b09c2/blt4ccc7a826cd95493/6822202b7cd2799769890371/2024_Get_the_app-img_1.jpg" />
      </div>
      <div css={styles.content}>
        <div css={styles.darkTextContent}>
          <h2 css={styles.heading}>Get the app</h2>
          <h3 css={styles.subHeading}>
            Easy-breezy shopping? Our app makes it easier for you to find the products you&apos;ll
            love from the best UK small brands.
          </h3>
          <div css={styles.copy}>
            <a href={appStoreAppsFlyerUrl} onClick={trackDownloadAppClick} title="App Store">
              <IconAppStore height="40" width="121" />
            </a>
            <a href={googlePlayAppsFlyerUrl} onClick={trackDownloadAppClick} title="Google Play">
              <IconGooglePlay height="40" width="135" />
            </a>
          </div>
        </div>
      </div>
    </div>
  </Banner>
);

const IconList = ({ children, start }: IconListProps) => (
  <ol css={styles.iconsBulletList} start={start}>
    {children}
  </ol>
);

const IconItem = ({ icon, text }: { icon: string; text: string }) => (
  <li css={styles.iconsBulletItem}>
    <img alt="" css={styles.iconsBulletItemImg} src={icon} />
    <p css={styles.iconsBulletItemText}>{text}</p>
  </li>
);

const title = 'Download the Not On The High Street App';
const copy =
  "Download the free Not On The High Street App to share, save and receive all of our latest offers and updates. Plus, it's got a five-star rating, hurrah!";

const DownloadTheAppsPage = () => (
  <>
    <Seo description={copy} title={title} />
    <div css={styles.wrapper}>
      <HeroBanner />
    </div>

    <div css={styles.wrapper}>
      <div css={[styles.container, styles.contentReverse, styles.narrowContainer]}>
        <div css={styles.imageWrapper}>
          <BannerImage imgUrl="https://images.contentstack.io/v3/assets/blt63f2f7afd19b09c2/blt694e12b877b5504c/6822205edb20547285b41ef4/2024_Get_the_app-img_2.jpg" />
        </div>
        <div css={[styles.content, styles.indentedContent]}>
          <IconList>
            <IconItem icon={icon1} text="Get order update notifications directly to your phone" />
            <IconItem
              icon={icon2}
              text="Save all your favourite finds with just one tap of the heart icon"
            />
            <IconItem icon={icon3} text="Share products easily on social media and WhatsApp" />
          </IconList>
        </div>
      </div>
    </div>

    <div css={styles.wrapper}>
      <div css={[styles.container, styles.narrowContainer]}>
        <div css={styles.imageWrapper}>
          <BannerImage imgUrl="https://images.contentstack.io/v3/assets/blt63f2f7afd19b09c2/blta0582982b500badd/68222087a66a226f6016cdce/2024_Get_the_app-img_3.jpg" />
        </div>
        <div css={[styles.content, styles.indentedContent]}>
          <IconList start={4}>
            <IconItem icon={icon4} text="Enjoy speedy checkout with Apple Pay" />
            <IconItem icon={icon5} text="Message sellers directly about your order" />
            <IconItem icon={icon6} text="Be the first to find out about sales and discounts" />
          </IconList>
        </div>
      </div>
    </div>

    <div css={styles.wrapper}>
      <div css={styles.qrCode}>
        <img alt="QR code to download the app" src={appQRCode} />
        <p>Scan me to download the app</p>
      </div>
    </div>
  </>
);

export default DownloadTheAppsPage;
